'use client';

import { type CSSProperties, type ReactNode } from 'react';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { Text } from '../../../../../components/text';

type FlatToolCardProps = {
  title: string;
  tone?: 'default' | 'plan' | 'agent' | 'ask';
  isRunning?: boolean;
  durationMs?: number;
  headerActions?: ReactNode;
  children?: ReactNode;
};

const runningLabelShimmerStyle: CSSProperties = {
  background:
    'linear-gradient(90deg, var(--colors-fg-muted) 25%, var(--colors-fg-subtle) 50%, var(--colors-fg-muted) 75%)',
  backgroundSize: '200% 100%',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  animationTimingFunction: 'linear',
};

export function FlatToolCard({
  title,
  tone = 'default',
  isRunning = false,
  durationMs,
  headerActions,
  children,
}: FlatToolCardProps) {
  const hasBody = Boolean(children);

  return (
    <Box
      borderWidth="1px"
      borderColor={
        tone === 'plan'
          ? 'border.decorative.yellow'
          : tone === 'agent'
            ? 'border.decorative.gray'
            : tone === 'ask'
              ? 'border.decorative.green'
              : 'border.neutral.muted'
      }
      borderRadius="md"
      bg={
        tone === 'plan'
          ? 'bg.decorative.yellow'
          : tone === 'agent'
            ? 'bg.decorative.gray'
            : tone === 'ask'
              ? 'bg.decorative.green'
              : 'transparent'
      }
      overflow="hidden"
      width="100%"
      minW={0}
      data-running={isRunning || undefined}
    >
      <HStack
        justify="space-between"
        alignItems="center"
        gap={2}
        px={3}
        minH="36px"
        minW={0}
        py={2}
        borderBottomWidth={hasBody ? '1px' : '0'}
        borderBottomColor={
          tone === 'plan'
            ? 'border.decorative.yellow'
            : tone === 'agent'
              ? 'border.decorative.gray'
              : tone === 'ask'
                ? 'border.decorative.green'
                : 'border.neutral.muted'
        }
      >
        <Box minW={0}>
          <Text
            fontSize="xs"
            fontWeight="medium"
            color={
              isRunning
                ? 'fg.muted'
                : tone === 'plan'
                  ? 'fg.decorative.yellow'
                  : tone === 'agent'
                    ? 'fg.decorative.gray'
                    : tone === 'ask'
                      ? 'fg.decorative.green'
                      : 'fg.default'
            }
            lineClamp={2}
            animationStyle={isRunning ? 'shimmer' : undefined}
            style={isRunning ? runningLabelShimmerStyle : undefined}
          >
            {title}
          </Text>
        </Box>
        <HStack gap={1} alignItems="center" flexShrink={0}>
          {durationMs !== undefined && !isRunning && (
            <Text fontSize="xs" color="fg.muted">
              {`${durationMs}ms`}
            </Text>
          )}
          {headerActions}
        </HStack>
      </HStack>

      {hasBody && (
        <Box px={3} py={2} minW={0}>
          <VStack alignItems="stretch" gap={2} width="100%" minW={0}>
            {children}
          </VStack>
        </Box>
      )}
    </Box>
  );
}
